import { Instagram, Mail } from "lucide-react";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";

const SOCIALS = [
  { icon: Instagram, label: "Instagram — @ak_the_storyteller", testid: "social-instagram", href: "https://instagram.com/ak_the_storyteller", external: true },
  { icon: Mail, label: "Email — adityakiran.filmmaker@gmail.com", testid: "social-email", href: "mailto:adityakiran.filmmaker@gmail.com", external: false },
];

const WORKS = [
  "PART 01 — THE RISE OF BHARGAV-RAM",
  "PART 02 — MACHU — THE FINDING FRIEND",
  "PART 03 — SAHADEV",
  "PART 04 — WHO IS THE SNIPER?",
  "PART 05 — GARUTMAN",
];

const About = () => (
  <section id="creator" data-testid="about-section" className="relative z-10 px-6 py-28 md:px-16 lg:px-24 lg:py-40">
    <SectionHeading index="08" title="THE CREATOR" />
    <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-2 lg:gap-24">
      <Reveal>
        <div className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-gradient-to-b from-[#191029] via-[#0b0812] to-[#030305] shadow-[0_30px_80px_rgba(0,0,0,0.7)]">
          <img
            src="/assets/creator-portrait-new.webp"
            alt="Pandrangi Aditya Kiran — Creator of the Cosmic Dreams Universe"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <p className="mt-6 font-mono text-[10px] tracking-[0.35em] text-[#D4AF37]/80 uppercase leading-relaxed">
          Author · Filmmaker · VFX Artist ·<br />
          Creator of the Cosmic Dreams Universe
        </p>
      </Reveal>
      <div>
        <Reveal delay={0.1}>
          <h3 className="font-serif text-3xl leading-tight tracking-wide text-[#F5F5FA] sm:text-4xl lg:text-5xl">
            PANDRANGI<br />ADITYA KIRAN
          </h3>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-sm md:text-base font-light leading-relaxed text-[#A0A0B0]">
            Pandrangi Aditya Kiran is an author, filmmaker, VFX artist, and the creator of the
            Cosmic Dreams Universe — an original interconnected fictional universe that brings
            together mythology, superheroes, futuristic technology, mysterious characters, and
            unexplored worlds.
          </p>
          <p className="mt-5 max-w-xl text-sm md:text-base font-light leading-relaxed text-[#A0A0B0]">
            With over seven years of experience in the visual effects industry, he has contributed
            to several international film and animation projects, developing expertise in cinematic
            storytelling, lighting, compositing, and visual world-building. This experience has
            shaped his creative vision of combining compelling narratives with immersive visual
            experiences.
          </p>
          <p className="mt-5 max-w-xl text-sm md:text-base font-light leading-relaxed text-[#A0A0B0]">
            Driven by a passion for storytelling and cinema, he created the Cosmic Dreams Universe
            as more than a book series — it is a long-term original intellectual property designed
            to evolve across novels, visual media, and future cinematic adaptations.
          </p>
          <p className="mt-5 max-w-xl text-sm md:text-base font-light leading-relaxed text-[#A0A0B0]">
            As both an author and filmmaker, his goal is to craft stories that inspire curiosity,
            imagination, and exploration, inviting audiences into a universe where mythology,
            science fiction, and human emotion exist as one interconnected saga.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <blockquote className="mt-10 border-l border-[#D4AF37]/50 pl-6">
            <p className="text-lg md:text-xl leading-relaxed text-[#F5F5FA]/90">
              “I don't want to simply tell stories. I want to build worlds that people can enter,
              explore, and dream beyond.”
            </p>
            <footer className="mt-3 font-mono text-[10px] tracking-[0.3em] text-[#D4AF37]/70 uppercase">
              — Pandrangi Aditya Kiran
            </footer>
          </blockquote>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="mt-10">
            <span className="font-mono text-[10px] tracking-[0.4em] text-[#606070] uppercase">The Saga So Far</span>
            <ul className="mt-4 space-y-2">
              {WORKS.map((w) => (
                <li key={w} className="font-mono text-[11px] tracking-[0.2em] text-[#A0A0B0]">
                  <span className="mr-3 text-[#D4AF37]/60">✦</span>{w}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.5}>
          <div className="mt-10 flex gap-4">
            {SOCIALS.map(({ icon: Icon, label, testid, href, external }) => (
              <a
                key={testid}
                data-testid={testid}
                href={href}
                aria-label={label}
                {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#A0A0B0] transition-all duration-500 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
              >
                <Icon size={16} strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default About;
